import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity } from '../api';
import { HelpPanel } from '../HelpPanel';
import { SCORING_IN_PROGRESS } from '../helpContent';
import './ScoringQueue.css';

/**
 * Opportunities → Scoring In Progress.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/opportunities-scoring-in-progress.ejs`
 * (586 lines) — its own view file, not a variant of the queue.
 *
 * It shares `ScoringQueue.css` because the THREE source views ship the same style block
 * verbatim; that is one visual language, not one screen. The screens themselves stay
 * separate, which is the point — collapsing them into a single parameterised table is what
 * went wrong the first time (§1.2.1), and it cost this screen its Progress column, its
 * project grouping and its filters.
 *
 * WHAT MAKES THIS SCREEN DIFFERENT FROM THE QUEUE:
 *   · a PROGRESS column — answered / total, a bar, and a percentage
 *   · the action is 🔄 Continue Scoring, not ▶️ Start
 *   · narrower Project column (250px vs 300px) to make room for Progress
 *   · ⏳ empty state, with its own wording
 *
 * MEMBERSHIP: status `Scoring in Progress` AND at least one answer recorded. An opportunity
 * whose form was merely opened has none, and belongs on the queue — the two screens split
 * on that count so the same row never appears on both.
 */

type Row = PdOpportunity & Record<string, unknown>;

interface ProjectGroup {
  projectId: string;
  description: string;
  opportunities: Row[];
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const shortDate = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString() : '');

/** Source :165–169 — four bands, and the order matters: 75 / 50 / 25 / below. */
function progressColour(pct: number): string {
  if (pct >= 75) return '#4caf50';
  if (pct >= 50) return '#2196f3';
  if (pct >= 25) return '#ff9800';
  return '#f44336';
}

export function ScoringInProgress() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [filterProject, setFilterProject] = useState('');
  const [filterOpportunity, setFilterOpportunity] = useState('');
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.scoring.inProgress();
      setRows((data.opportunities as Row[]) ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Measured height — the shell never bounds its content area, so `100%` collapses. */
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const groups = useMemo<ProjectGroup[]>(() => {
    const byProject = new Map<string, ProjectGroup>();
    for (const o of rows) {
      const projectId = s(o.project_id) || 'No Project';
      let g = byProject.get(projectId);
      if (!g) {
        g = { projectId, description: s(o.project_description), opportunities: [] };
        byProject.set(projectId, g);
      }
      g.opportunities.push(o);
    }
    return [...byProject.values()].sort((a, b) => a.projectId.localeCompare(b.projectId));
  }, [rows]);

  const visible = useMemo(() => {
    const p = filterProject.trim().toLowerCase();
    const q = filterOpportunity.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        opportunities: q
          ? g.opportunities.filter(
              (o) =>
                s(o.opportunity_id).toLowerCase().includes(q) ||
                s(o.description).toLowerCase().includes(q),
            )
          : g.opportunities,
      }))
      .filter((g) => (p ? g.projectId.toLowerCase().includes(p) : true))
      .filter((g) => g.opportunities.length > 0);
  }, [groups, filterProject, filterOpportunity]);

  const visibleCount = visible.reduce((n, g) => n + g.opportunities.length, 0);

  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="sq-page"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="sq-fixed">
        <div className="sq-header">
          <div>
            <h1 className="sq-title">Scoring In Progress</h1>
            <p className="sq-subtitle">Opportunities currently being scored</p>
          </div>
          <HelpPanel help={SCORING_IN_PROGRESS} />
        </div>

        <div className="sq-quick">
          <button
            type="button"
            title="Expand All"
            onClick={() => setOpen(new Set(groups.map((g) => g.projectId)))}
          >
            ⬇️ Expand All
          </button>
          <button type="button" title="Collapse All" onClick={() => setOpen(new Set())}>
            ⬆️ Collapse All
          </button>
        </div>

        {rows.length > 0 ? (
          <div className="sq-filters">
            <div className="sq-filter">
              <label htmlFor="ip-fp">Project:</label>
              <input
                id="ip-fp"
                type="text"
                placeholder="Filter..."
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              />
            </div>
            <div className="sq-filter">
              <label htmlFor="ip-fo">Opportunity:</label>
              <input
                id="ip-fo"
                type="text"
                placeholder="Filter..."
                value={filterOpportunity}
                onChange={(e) => setFilterOpportunity(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="sq-clear"
              onClick={() => {
                setFilterProject('');
                setFilterOpportunity('');
              }}
            >
              Clear
            </button>
            <span className="sq-count">
              {visibleCount} of {rows.length} opportunities
            </span>
          </div>
        ) : null}
      </div>

      <div className="sq-body">
        {error ? <div className="sq-error">{error}</div> : null}

        <div className="sq-card">
          {loading ? (
            <div className="sq-empty">
              <p>Loading…</p>
            </div>
          ) : rows.length === 0 ? (
            /* Source :94 — wording preserved exactly. */
            <div className="sq-empty">
              <div className="sq-empty-icon">⏳</div>
              <h3>No Opportunities In Progress</h3>
              <p>Opportunities will appear here when scoring is started</p>
            </div>
          ) : (
            <>
              <div className="sq-thead-wrap">
                <table className="sq-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th style={{ width: 250 }}>Project / Opportunity</th>
                      <th style={{ width: 300 }}>Description</th>
                      <th style={{ width: 100, textAlign: 'center' }}>Progress</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Owner</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Submitted</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="sq-tbody-wrap">
                <table className="sq-table">
                  <tbody>
                    {visible.map((g) => {
                      const isOpen = open.has(g.projectId);
                      const n = g.opportunities.length;
                      return (
                        <Fragment key={g.projectId}>
                          <tr className="sq-project-row" onClick={() => toggle(g.projectId)}>
                            <td style={{ width: 40 }}>
                              <span className="sq-expand">{isOpen ? '▼' : '▶'}</span>
                            </td>
                            <td style={{ width: 250, fontSize: 12 }}>
                              📁 <strong>{g.projectId}</strong>
                            </td>
                            <td className="sq-cell-ellipsis" style={{ width: 300, fontSize: 11 }}>
                              {g.description}
                            </td>
                            <td style={{ width: 100 }} />
                            <td style={{ width: 120 }} />
                            <td style={{ width: 120 }} />
                            <td className="sq-cell-center" style={{ width: 120, fontSize: 11 }}>
                              {n} {n === 1 ? 'Opportunity' : 'Opportunities'}
                            </td>
                          </tr>

                          {isOpen
                            ? g.opportunities.map((o) => {
                                const answered = Number(o.answered_questions ?? 0);
                                const totalQ = Number(o.total_questions ?? 0);
                                const pct = totalQ > 0 ? Math.round((answered / totalQ) * 100) : 0;
                                const colour = progressColour(pct);
                                return (
                                  <tr key={s(o.opportunity_id)} className="sq-opportunity-row">
                                    <td style={{ width: 40 }} />
                                    <td
                                      className="sq-cell-indent"
                                      style={{ width: 250, fontSize: 11 }}
                                    >
                                      📂 <strong>{s(o.opportunity_id)}</strong>
                                    </td>
                                    <td
                                      className="sq-cell-ellipsis"
                                      style={{ width: 300, fontSize: 11 }}
                                    >
                                      {s(o.description)}
                                    </td>
                                    {/* Source :180 — count, bar and percentage stacked. */}
                                    <td className="sq-cell-center" style={{ width: 100 }}>
                                      <div className="sq-progress">
                                        <strong style={{ fontSize: 12, color: colour }}>
                                          {answered} / {totalQ}
                                        </strong>
                                        <div className="sq-progress-track">
                                          <div
                                            className="sq-progress-fill"
                                            style={{ width: `${pct}%`, backgroundColor: colour }}
                                          />
                                        </div>
                                        <span className="sq-progress-pct">{pct}%</span>
                                      </div>
                                    </td>
                                    <td
                                      className="sq-cell-center"
                                      style={{ width: 120, fontSize: 11 }}
                                    >
                                      {s(o.owner_name)}
                                    </td>
                                    <td
                                      className="sq-cell-center sq-cell-muted"
                                      style={{ width: 120, fontSize: 11 }}
                                    >
                                      {shortDate(o.submitted_date)}
                                    </td>
                                    <td className="sq-cell-center" style={{ width: 120 }}>
                                      <div className="sq-actions">
                                        <button
                                          type="button"
                                          className="sq-icon-btn"
                                          title="Continue Scoring"
                                          onClick={() =>
                                            navigate(
                                              `/portfolio_definition/opportunities/scoring-queue/${encodeURIComponent(s(o.opportunity_id))}`,
                                            )
                                          }
                                        >
                                          🔄
                                        </button>
                                        <button
                                          type="button"
                                          className="sq-icon-btn"
                                          title="View the answers so far — read only"
                                          onClick={() =>
                                            /* Opens the Scoring Details tab, so the answers
                                               already given can be read without Continue —
                                               the only other action here, and the one that
                                               changes them. Owner's ask, 2026-09-30. */
                                            navigate(
                                              `/portfolio_definition/opportunities/opportunity-registry/${encodeURIComponent(s(o.opportunity_id))}?tab=scoring`,
                                            )
                                          }
                                        >
                                          👁️
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
